/**
 * @author Christian Künne <christian.kuenne@tomtom.com>
 */

import React, { Component } from 'react'
import cx from 'classnames'
import PropTypes from 'prop-types'

import FilterBarItem from './filter-bar-item'
import FilterBarSpacer from './filter-bar-spacer'

import styles from './filter-bar.module.css'

const propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
    itemClassName: PropTypes.string
}
const defaultProps = {
    children: []
}

/**
 * Generic filter bar that should contain children components for filter actions (i.e. dropdowns and searchboxes).
 * Children are automatically wrapped in a <code><FilterBarItem/></code> for proper layouting.
 * A custom CSS class can be used with the <code>itemClassName</code> property for all filter bar items set on the
 * <code><FilterBar/></code> component or with the <code>className</code> property for individual filter bar children
 * which are applied to according <code><FilterBarItem/></code> wrapper component.
 *
 * @see FilterBarItem
 * @see FilterBarSeparator
 * @see FilterBarSpacer
 *
 * {@link https://confluence.tomtomgroup.com/display/TTB/Molecules#Molecules-FilterBar}
 *
 * @example
 * <FilterBar className='myCustomFilterBarClass' itemClassName='myCustomFilterBarItemClass'>
 *     <Button className='myIndividualCustomFilterBarItemClass'>Do something</Button>
 *     <FilterBarSeparator />
 *     <Dropdown ... />
 *     <SearchField ... />
 *     <FilterBarSpacer />
 *     <AngleDownIcon/>
 * </FilterBar>
 *
 * @property {Array|string|ReactElement} [props.children] - The child elements of this component
 * @property {string} [props.className] - A CSS class to append to the component
 * @property {string} [props.itemClassName] - A CSS to be appended to a children
 */
class FilterBar extends Component {
    constructor(props) {
        super(props)

        this.renderItem = this.renderItem.bind(this)
    }

    render() {
        const { children, className } = this.props
        const composedClassName = cx(styles['filter-bar'], className, 't3sel-filter-bar')

        return <div className={composedClassName}>{React.Children.toArray(children).map(this.renderItem)}</div>
    }

    renderItem(item, idx) {
        const { itemClassName } = this.props
        const itemKey = `filter_bar_item_${idx}`

        if (item.type === FilterBarItem || item.type === FilterBarSpacer) {
            return React.cloneElement(item, {
                className: cx(styles[itemClassName], item.props.className),
                key: itemKey
            })
        }

        return (
            <FilterBarItem key={itemKey} className={styles[itemClassName]}>
                {item}
            </FilterBarItem>
        )
    }
}

FilterBar.propTypes = propTypes
FilterBar.defaultProps = defaultProps

export default FilterBar
